import { type ComponentProps, type JSX, type JSXElementConstructor } from 'react'
import { Link as MuiLink, type LinkProps as MuiLinkProps } from '@mui/material'
import { LaunchIcon } from '@dhl-ui/icons'
import { spacing } from '../../theme'

export type BaseLinkProps<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<never>> = Omit<
  MuiLinkProps,
  'classes' | 'sx'
> & {
  /**
   * title of the link, optional
   */
  title?: string
  /**
   * external: when referring to an external source
   * when set, a launch icon will be appended to indicate
   * @default false
   */
  external?: boolean
  /**
   * inline; use when placing a link in other text
   * @default false
   */
  inline?: boolean
  to?: string
} & ComponentProps<T>

export const BaseLink = <T extends keyof JSX.IntrinsicElements | JSXElementConstructor<never>>({
  children,
  external,
  inline,
  target,
  rel,
  color = 'primary',
  variant = 'body1',
  ...props
}: BaseLinkProps<T>) => (
  <MuiLink
    color={color}
    {...props}
    {...(external
      ? {
          target: target ?? '_blank',
          rel: rel ?? 'noopener noreferrer',
        }
      : {})}
    {...(inline
      ? {
          mx: spacing(0.4),
        }
      : {
          variant,
        })}
  >
    {children}
    {external && <LaunchIcon style={{ verticalAlign: 'middle' }} fontSize="inherit" />}
  </MuiLink>
)
